// The Repos landing page: the namespaces this community governs, the
// repositories in the one selected, who holds its namespace rights, and the
// grants departed members left behind.

import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FolderGit2, Plus } from "lucide-react";

import { CopyButton } from "@/components/CopyButton";
import { type Column, DataTable, useSortedRows } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { InfoTip } from "@/components/InfoTip";
import { NamedDid } from "@/components/NamedDid";
import { PageHeader } from "@/components/PageHeader";
import { fetchActivePolicy } from "@/lib/policies-api";
import { useNameBook } from "@/lib/names";
import {
  administersNamespace,
  mayCreateIn,
  useCapabilities,
  useIsGitCommunityAdmin,
} from "@/lib/viewer";
import type {
  GitNsNamespaceRow,
  GitNsRepoRow,
  GitNsRightRow,
} from "@/lib/wire-types";

import { reprojectTask, type SignedTask, unbindTask } from "./actions";
import {
  fetchBreakGlass,
  fetchIssuedByDeparted,
  fetchNamespaces,
  fetchRepos,
  fetchRights,
  gitNsKeys,
} from "./api";
import { AdoptDialog, CreateDialog, GrantDialog, ReseatDialog } from "./dialogs";
import {
  isPersonal,
  isServiceGrant,
  kindLabel,
  modeLabel,
  namespaceFindings,
  repoStatus,
  shortName,
} from "./model";
import {
  BIND_PATH,
  BootstrapDots,
  BootstrapLegend,
  BREAK_GLASS_PATH,
  DEPARTED_PATH,
  errorMessage,
  readErrorMessage,
  namespacePath,
  POLICY_PATH,
  repoPath,
  SignTaskDialog,
  ToneChip,
} from "./ui";

type Dialog =
  | { kind: "grant"; resource: string; right: "git.ns.admin" | "git.repo.create" | "git.repo.own"; title?: string }
  | { kind: "adopt"; resource?: string; namespaceResource?: string }
  | { kind: "create"; ns: GitNsNamespaceRow }
  | { kind: "reseat"; ns: GitNsNamespaceRow }
  | { kind: "sign"; task: SignedTask };

function PersonalAccountHint({ ns }: { ns: GitNsNamespaceRow }) {
  return (
    <div className="finding warn">
      <strong>Forge roles collapse to write on a personal account</strong>
      <span>
        Only the account holder can be admin there, and only they can create a
        repository, so creation is theirs alone. Ownership in the VTC still decides
        who may grant commit rights.
      </span>
      <details>
        <summary>Move to an organisation</summary>
        <ol className="gitns-steps-list">
          <li>Create an organisation on {ns.forge} and make the account holder an owner.</li>
          <li>Bind the organisation here and install the community's App on it.</li>
          <li>
            Transfer each repository from {ns.owner} to the organisation in its forge
            settings.
          </li>
          <li>
            The bridge reports each transfer as drift and the VTC moves its rights
            and Trust Registry records to the new name.
          </li>
        </ol>
      </details>
    </div>
  );
}

/** What the bridge last reported about its standing on the owner. Every
 *  member is optional: only what was reported is shown. */
function ForgeStatusLine({ ns }: { ns: GitNsNamespaceRow }) {
  const fs = ns.forgeStatus!;
  const parts: string[] = [];
  if (fs.appName || fs.appSlug) parts.push(`App ${fs.appName ?? fs.appSlug}`);
  if (fs.installationId) parts.push(`installation #${fs.installationId}`);
  if (fs.appRegistration) parts.push(`registration: ${fs.appRegistration}`);
  if (fs.requiredWorkflow != null)
    parts.push(fs.requiredWorkflow ? "required workflow in force" : "required workflow not in force");
  if (fs.bridgePostedCheck != null)
    parts.push(fs.bridgePostedCheck ? "bridge-posted check ready" : "bridge-posted check not ready");
  if (fs.missingPermissions.length === 0 && fs.installationId) parts.push("all permissions granted");
  if (parts.length === 0) return null;
  return (
    <p className="muted gitns-small">
      {parts.join(" · ")}
      {fs.reportedAt && <> · reported {new Date(fs.reportedAt).toLocaleDateString()}</>}
    </p>
  );
}

function NamespaceCard({
  ns,
  repos,
  rights,
  rightsError,
  selected,
  policyVersion,
  onUnbind,
  onReseat,
  onReproject,
}: {
  ns: GitNsNamespaceRow;
  repos: GitNsRepoRow[];
  /** `null` while the rights are loading or could not be read: then nothing
   *  that depends on them — creators, the service grant — is claimed. */
  rights: GitNsRightRow[] | null;
  rightsError: unknown;
  selected: boolean;
  policyVersion: number | null | undefined;
  onUnbind: () => void;
  onReseat: () => void;
  onReproject: () => void;
}) {
  const book = useNameBook();
  // Reseat is signed with the community-administrator capability alone, so
  // nobody else is offered it. Unbind stays: a namespace admin may sign it too.
  const superAdmin = useIsGitCommunityAdmin();
  // Unbinding and re-projecting are the namespace's own administrators' to
  // sign (`git.ns.admin` at this namespace, or community-wide).
  const administers = administersNamespace(useCapabilities(), ns.resource);
  const managed = repos.filter((r) => r.state !== "unmanaged" && r.state !== "detached").length;
  const unmanaged = repos.filter((r) => r.state === "unmanaged").length;
  const creators = rights?.filter(
    (r) => r.resource === ns.resource && r.right === "git.repo.create",
  ).length;
  const service = rights?.find((r) => isServiceGrant(r, ns));
  const findings = namespaceFindings(ns);
  const stale = repos.filter((r) => r.roleMapStale).length;
  const bridgeBound = ns.mode === "bridge" && ns.state === "bound";
  const headingId = `gitns-ns-${ns.id}`;

  return (
    <article
      className={selected ? "card gitns-ns selected" : "card gitns-ns"}
      aria-labelledby={headingId}
      aria-current={selected ? "true" : undefined}
    >
      <div className="gitns-ns-head">
        <h3 id={headingId} className="gitns-ns-name">
          <Link to={namespacePath(ns.id)}>{ns.resource}</Link>
        </h3>
        <ToneChip tone={ns.kind === "organization" ? "accent" : "neutral"}>
          {kindLabel(ns)}
        </ToneChip>
        <span
          className={`gitns-mode ${ns.installationRemoved ? "bad" : ns.mode === "bridge" && ns.state === "bound" ? "ok" : ""}`}
        >
          {modeLabel(ns)}
        </span>
      </div>

      <div className="gitns-stats">
        <span>
          <b>{managed}</b> managed {managed === 1 ? "repo" : "repos"}
        </span>
        {unmanaged > 0 && (
          <span>
            <b>{unmanaged}</b> unmanaged
          </span>
        )}
        <span>
          <b>{ns.admins.length}</b> namespace {ns.admins.length === 1 ? "admin" : "admins"}
        </span>
        {isPersonal(ns) ? (
          <span>
            Repo creation: <b>account holder only</b>
          </span>
        ) : creators === undefined ? (
          <span className="muted">Repo creators: {rightsError ? "not readable" : "…"}</span>
        ) : (
          <span>
            <b>{creators}</b> repo {creators === 1 ? "creator" : "creators"}
          </span>
        )}
      </div>

      <p className="muted gitns-small">
        Policy:{" "}
        <Link to={POLICY_PATH}>
          {policyVersion ? `git namespace policy v${policyVersion}` : "git namespace policy"}
        </Link>
        {policyVersion === null && " (none active: every change is refused)"}
        {ns.boundAt && <> · bound {new Date(ns.boundAt).toLocaleDateString()}</>}
        {" · by "}
        <NamedDid did={ns.boundBy} book={book} nameOnly={!!book.nameOf(ns.boundBy)} />
      </p>

      <p className="muted gitns-small">
        Drift: rulesets <b>enforce</b> · roles <b>{ns.roleDrift}</b>
        {" · "}grants of departed members{" "}
        <b>{ns.cascadeOnDeparture ? "revoked with them" : "kept for review"}</b>
      </p>

      {bridgeBound && ns.roleMap && (
        <p className="muted gitns-small" aria-label="Forge role map">
          Forge roles: owner <b>{ns.roleMap.own}</b> · maintainer <b>{ns.roleMap.maintain}</b> ·
          committer <b>{ns.roleMap.commit === "none" ? "no role" : ns.roleMap.commit}</b> ·
          namespace admin <b>no role</b>
          {" — "}
          {`as the bridge reported it${ns.roleMapReportedAt ? ` on ${new Date(ns.roleMapReportedAt).toLocaleDateString()}` : ""}; a repository may have its own.`}
        </p>
      )}
      {bridgeBound && !ns.roleMap && (
        <div className="finding warn">
          <strong>Role map unknown</strong>
          <span>
            The bridge serving this namespace has not reported which forge role each right
            projects to, so the VTC assumes none — not even the default. Until it does, drift
            cannot be adopted (the VTC cannot tell which right a forge role stands for), and every
            role revert weighs as revoking ownership. A bridge reports its map when it starts
            serving the namespace and whenever it reconnects to the VTC; one older than
            git-ns/bridge/event 0.3 never does.
          </span>
        </div>
      )}
      {stale > 0 && (
        <div className="finding warn">
          <strong>
            {stale} {stale === 1 ? "repository" : "repositories"} projected under an earlier role map
          </strong>
          <span>
            The bridge's role map changed. The VTC has queued their re-projection; this clears
            as the bridge confirms each one.
          </span>
        </div>
      )}

      {ns.forgeStatus && <ForgeStatusLine ns={ns} />}

      {ns.bridgeDid && (
        <p className="muted gitns-small">
          Bridge <NamedDid did={ns.bridgeDid} book={book} />
          {rights === null ? (
            rightsError ? (
              <> — whether it holds its service grant could not be read ({readErrorMessage(rightsError)}).</>
            ) : null
          ) : service ? (
            <>
              {" "}
              holds <code>git.commit.sign</code> on {ns.resource} as a service grant
              from the community, to re-sign Dependabot pull requests (never ones
              that touch workflows). Read-only: it ends with the binding.
            </>
          ) : (
            ns.state === "bound" && (
              <>
                {" "}
                holds no service grant: the policy refused it, so Dependabot pull
                requests will not pass the check until someone re-signs them.
              </>
            )
          )}
        </p>
      )}

      {findings.map((f) => (
        <div key={f.title} className={`finding ${f.tone === "danger" ? "error" : "warn"}`}>
          <strong>{f.title}</strong>
          <span>{f.detail}</span>
        </div>
      ))}
      {isPersonal(ns) && <PersonalAccountHint ns={ns} />}
      <div className="gitns-card-actions">
        {ns.headless && ns.state === "bound" && superAdmin && (
          <>
            <span className="muted gitns-small">Needs a community administrator.</span>
            <button
              type="button"
              className="secondary sm destructive"
              onClick={onReseat}
              aria-label={`Reseat ${ns.resource}`}
            >
              Reseat
            </button>
          </>
        )}
        {administers && bridgeBound && !ns.installationRemoved && (
          <button
            type="button"
            className="secondary sm"
            onClick={onReproject}
            aria-label={`Re-project roles on ${ns.resource}`}
            title="Have the bridge re-apply every repository's forge roles under its current role map. No right changes."
          >
            Re-project roles
          </button>
        )}
        {administers && (
          <button
            type="button"
            className="secondary sm destructive"
            onClick={onUnbind}
            aria-label={`Unbind ${ns.resource}`}
          >
            Unbind
          </button>
        )}
      </div>
    </article>
  );
}

type RepoSortKey = "name" | "maintainers" | "committers" | "sync";

const REPO_COLUMNS: readonly Column<RepoSortKey>[] = [
  { key: "repository", label: "Repository", sortKey: "name" },
  {
    key: "owners",
    label: "Owners",
    tip: (
      <>
        Hold git.repo.own: may grant owner, maintainer and committer on the
        repository, and get the forge role the bridge maps ownership to.
      </>
    ),
  },
  {
    key: "maintainers",
    label: "Maintainers",
    sortKey: "maintainers",
    tip: (
      <>
        Hold git.repo.maintain: may merge, and get the forge's maintain role. They
        cannot grant rights.
      </>
    ),
  },
  {
    key: "committers",
    label: "Committers",
    sortKey: "committers",
    tip: (
      <>
        Hold git.commit.sign: their DID-signed commits pass the commit-trust check.
        They need no forge access — they contribute through fork pull requests.
      </>
    ),
  },
  {
    key: "commit trust",
    label: "Commit trust",
    tip: (
      <>
        The four setup steps that make the forge refuse unsigned or untrusted
        commits: workflow, keyring, variables, required check. A filled dot is in
        place, an empty one missing, a dashed one not used by this repository's
        setup. Hover a dot for details.
      </>
    ),
  },
  {
    key: "sync",
    label: "Forge sync",
    sortKey: "sync",
    tip: (
      <>
        Whether the forge still matches what the VTC projected — roles and branch
        protection. Drift means someone changed it on the forge directly.
      </>
    ),
  },
];

const NSR_COLUMNS: readonly Column[] = [
  { key: "right", label: "Right", className: "gitns-col-nsr-right" },
  { key: "held", label: "Held by" },
  {
    key: "actions",
    label: <span className="visually-hidden">Actions</span>,
    className: "gitns-col-actions",
  },
];

function ReposTable({
  ns,
  repos,
  onAdopt,
  onAssignOwner,
}: {
  ns: GitNsNamespaceRow;
  repos: GitNsRepoRow[];
  onAdopt: (resource: string) => void;
  onAssignOwner: (resource: string) => void;
}) {
  const book = useNameBook();
  // Adopting and naming an owner are grants of `git.repo.own`, which the
  // namespace's administrators make.
  const administers = administersNamespace(useCapabilities(), ns.resource);
  const sorted = useSortedRows<GitNsRepoRow, RepoSortKey>(repos, (r, key) => {
    const unmanaged = r.state === "unmanaged";
    switch (key) {
      case "name":
        return shortName(r.resource);
      case "maintainers":
        return unmanaged ? null : r.maintainers;
      case "committers":
        return unmanaged ? null : r.committers;
      case "sync":
        return repoStatus(r).label;
    }
  });
  if (repos.length === 0) {
    return (
      <EmptyState icon={FolderGit2} title={`No repositories in ${ns.resource} yet`}>
        {ns.mode === "bridge"
          ? "Repositories appear as they are created or adopted, and ones the bridge finds on the forge are listed here as unmanaged."
          : "In manual mode the VTC knows only repositories adopted through it. Adopt one to bring it under governance."}
      </EmptyState>
    );
  }
  return (
    <div className="table-scroll">
      <DataTable columns={REPO_COLUMNS} sort={sorted.sort} onSort={sorted.onSort}>
        {sorted.rows.map((r) => {
          const status = repoStatus(r);
          const unmanaged = r.state === "unmanaged";
          return (
            <tr key={r.id}>
              <td>
                <Link to={repoPath(r.resource)} className="gitns-mono">
                  {shortName(r.resource)}
                </Link>
                <div className="muted gitns-small">
                  {r.visibility}
                  {r.createdBy && (
                    <>
                      {" · created by "}
                      <NamedDid did={r.createdBy} book={book} nameOnly={!!book.nameOf(r.createdBy)} />
                    </>
                  )}
                </div>
              </td>
              <td>
                {r.state === "orphaned" ? (
                  <span className="gitns-bad">Orphaned · reassigned to admins</span>
                ) : unmanaged || r.owners.length === 0 ? (
                  "—"
                ) : (
                  r.owners.map((o) => (
                    <div key={o}>
                      <NamedDid did={o} book={book} />
                    </div>
                  ))
                )}
              </td>
              <td className="tabular">{unmanaged ? "—" : r.maintainers}</td>
              <td className="tabular">{unmanaged ? "—" : r.committers}</td>
              <td>
                <BootstrapDots ns={ns} repo={r} />
              </td>
              <td>
                <div className="gitns-sync">
                  <ToneChip tone={status.tone} title={r.lastError ?? undefined}>
                    {status.label}
                  </ToneChip>
                  {administers && status.action === "adopt" && (
                    <button
                      type="button"
                      className="link"
                      onClick={() => onAdopt(r.resource)}
                      aria-label={`Adopt ${shortName(r.resource)}`}
                    >
                      Adopt
                    </button>
                  )}
                  {administers && status.action === "assignOwner" && (
                    <button
                      type="button"
                      className="link"
                      onClick={() => onAssignOwner(r.resource)}
                      aria-label={`Assign an owner to ${shortName(r.resource)}`}
                    >
                      Assign owner
                    </button>
                  )}
                  {(status.action === "resolve" || status.action === "view") && (
                    <Link
                      to={`${repoPath(r.resource)}${status.action === "resolve" ? "#drift" : ""}`}
                      aria-label={`${status.actionLabel} ${shortName(r.resource)}`}
                    >
                      {status.actionLabel}
                    </Link>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </DataTable>
    </div>
  );
}

function NamespaceRights({
  ns,
  rights,
  onGrant,
}: {
  ns: GitNsNamespaceRow;
  rights: GitNsRightRow[];
  onGrant: (right: "git.ns.admin" | "git.repo.create") => void;
}) {
  const book = useNameBook();
  const holders = (right: string) =>
    rights.filter((r) => r.resource === ns.resource && r.right === right);
  const admins = holders("git.ns.admin");
  const creators = holders("git.repo.create");
  // Granting either is the namespace administrators' (git.ns.admin here or
  // community-wide); anyone else is not offered it.
  const bound = ns.state === "bound" && administersNamespace(useCapabilities(), ns.resource);

  const list = (rows: GitNsRightRow[]) =>
    rows.length === 0 ? (
      <span className="muted">Nobody</span>
    ) : (
      rows.map((r) => (
        <span key={r.subject} className="gitns-holder-chip">
          <NamedDid did={r.subject} book={book} />
          <CopyButton value={r.subject} label="Copy DID" successMessage="DID copied" />
          {r.expiresAt && (
            <span className="muted gitns-small"> until {new Date(r.expiresAt).toLocaleDateString()}</span>
          )}
        </span>
      ))
    );

  return (
    <section className="card" aria-labelledby={`gitns-nsr-${ns.id}`}>
      <h3 id={`gitns-nsr-${ns.id}`}>Namespace rights in {ns.owner}</h3>
      <div className="table-scroll">
        <DataTable columns={NSR_COLUMNS} className="gitns-table gitns-nsr-table">
          <tr>
            <td>
              <span className="gitns-right-name">Namespace admin</span>
              <InfoTip label="About namespace admin">
                Can grant any right in {ns.resource} and manages its repositories —
                treat it like an org owner. Gives no role on the forge by itself.
                Granting it is destructive-class and needs a passkey step-up.
              </InfoTip>
              <div>
                <code className="gitns-right-admin gitns-small">git.ns.admin</code>
              </div>
            </td>
            <td>
              <div className="gitns-holders">{list(admins)}</div>
            </td>
            <td>
              <button
                type="button"
                className="secondary sm"
                disabled={!bound}
                title="Grant namespace admin to a current member"
                onClick={() => onGrant("git.ns.admin")}
              >
                Grant
              </button>
            </td>
          </tr>
          <tr>
            <td>
              <span className="gitns-right-name">Repo creator</span>
              <InfoTip label="About repo creator">
                May create new repositories in {ns.resource} through the VTC and becomes
                their first owner. Cannot pass the right on.
              </InfoTip>
              <div>
                <code className="gitns-right-create gitns-small">git.repo.create</code>
              </div>
            </td>
            <td>
              <div className="gitns-holders">
                {isPersonal(ns) ? (
                  <span>
                    The account holder only — a personal account cannot let anyone else
                    create a repository.
                  </span>
                ) : (
                  list(creators)
                )}
              </div>
            </td>
            <td>
              {!isPersonal(ns) && (
                <button
                  type="button"
                  className="secondary sm"
                  disabled={!bound}
                  title="Grant repo creator to a current member"
                  onClick={() => onGrant("git.repo.create")}
                >
                  Grant
                </button>
              )}
            </td>
          </tr>
        </DataTable>
      </div>
      <p className="muted gitns-small">
        A namespace admin can grant anything in the namespace — treat it like org
        owner. Granting it is destructive-class and needs a step-up; repo creators
        cannot pass their right on. Both go to current members only.
        {ns.state !== "bound" && " Nothing can be granted until the binding finishes."}
        {ns.state === "bound" &&
          !bound &&
          " Granting them takes git.ns.admin on this namespace."}
      </p>
    </section>
  );
}

/** Self-granted elevated rights (`git-ns/right/break-glass`), and how many are
 *  still waiting for another administrator. */
function BreakGlassCard() {
  const q = useQuery({ queryKey: gitNsKeys.breakGlass, queryFn: fetchBreakGlass, retry: false });
  const items = q.data?.items ?? [];
  const waiting = items.filter((i) => i.state === "unratified" || i.state === "pending").length;
  return (
    <section
      className={waiting > 0 ? "card gitns-departed warn" : "card gitns-departed"}
      aria-labelledby="gitns-breakglass-title"
    >
      <h3 id="gitns-breakglass-title">Break-glass grants</h3>
      {q.isPending && <p>Reading break-glass grants…</p>}
      {q.isError && (
        <p className="muted">
          Could not be read: {readErrorMessage(q.error)}. This is a failure to ask, not
          an empty list.
        </p>
      )}
      {q.data && (
        <p>
          {waiting > 0
            ? `${waiting} self-granted ${waiting === 1 ? "right is" : "rights are"} waiting for another administrator to ratify or revoke.`
            : items.length > 0
              ? "Every break-glass has been ratified or revoked."
              : "Nobody has broken the glass."}
        </p>
      )}
      {q.data && items.length > 0 && (
        <p>
          <Link to={BREAK_GLASS_PATH} className="button secondary">
            {waiting > 0 ? `Review ${waiting}` : "History"}
          </Link>
        </p>
      )}
    </section>
  );
}

function DepartedCard() {
  const q = useQuery({ queryKey: gitNsKeys.departed, queryFn: fetchIssuedByDeparted });
  const count = (q.data?.granters ?? []).reduce((n, g) => n + g.rights.length, 0);
  const people = q.data?.granters.length ?? 0;

  return (
    <section
      className={count > 0 ? "card gitns-departed warn" : "card gitns-departed"}
      aria-labelledby="gitns-departed-title"
    >
      <h3 id="gitns-departed-title">Issued by departed members</h3>
      {q.isPending && <p>Reading grants…</p>}
      {q.isError && (
        <p className="muted">
          Could not be read: {readErrorMessage(q.error)}. This is a failure to ask, not
          an empty list.
        </p>
      )}
      {q.data && count === 0 && (
        <EmptyState compact title="No live grant was issued by someone who has since left." />
      )}
      {q.data && count > 0 && (
        <>
          <p>
            {count} {count === 1 ? "grant" : "grants"} issued by {people}{" "}
            {people === 1 ? "person" : "people"} who left.{" "}
            {q.data.cascadeOnDeparture
              ? "The active policy revokes these (cascade_on_departure); they are listed until the revocation lands."
              : "Each grant is a delegation: it goes to review in the action list, and is withdrawn unless an administrator re-affirms it before the deadline."}
          </p>
          <p>
            <Link to={DEPARTED_PATH} className="button secondary">
              Review {count} {count === 1 ? "grant" : "grants"}
            </Link>
          </p>
        </>
      )}
    </section>
  );
}

export function Overview() {
  const [params] = useSearchParams();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const nsQ = useQuery({ queryKey: gitNsKeys.namespaces, queryFn: fetchNamespaces });
  const reposQ = useQuery({ queryKey: gitNsKeys.repos, queryFn: fetchRepos });
  const rightsQ = useQuery({ queryKey: gitNsKeys.rights, queryFn: fetchRights });
  const policyQ = useQuery({
    queryKey: ["policies", "active", "gitNamespace"],
    queryFn: () => fetchActivePolicy("gitNamespace"),
  });

  const namespaces = nsQ.data?.namespaces ?? [];
  const repos = reposQ.data?.repos ?? [];
  const rights = rightsQ.data?.rights ?? [];
  const selected =
    namespaces.find((n) => n.id === params.get("namespace")) ?? namespaces[0];
  const selectedRepos = useMemo(
    () =>
      selected
        ? repos.filter((r) => r.namespace === selected.id && r.state !== "detached")
        : [],
    [repos, selected],
  );
  // Detached rows, and any row whose namespace is no longer listed (an
  // unbound namespace's repositories outlive it as records).
  const detached = useMemo(
    () =>
      repos.filter(
        (r) => r.state === "detached" || !namespaces.some((n) => n.id === r.namespace),
      ),
    [repos, namespaces],
  );
  const roleDerived = rights.filter((r) => r.origin === "roleDerived").length;
  // What the viewer's own entry holds decides what is offered (phase C3: git
  // rights are capabilities on the ACL entry). The VTC still decides each
  // operation.
  const caps = useCapabilities();
  const gitAdmin = useIsGitCommunityAdmin();
  const mayCreate = !!selected && mayCreateIn(caps, selected.resource);
  const mayAdopt = !!selected && administersNamespace(caps, selected.resource);
  const policyVersion = policyQ.isSuccess ? (policyQ.data?.version ?? null) : undefined;

  return (
    <>
      <PageHeader
        title="Repos"
        lead={
          <>
            Git namespaces this community governs. Rights are held here, published to
            the Trust Registry, and applied on the forge by the community's bridge.
          </>
        }
        actions={
          (mayCreate || mayAdopt || gitAdmin) && (
            <>
              {mayCreate && (
                <button
                  type="button"
                  className="secondary"
                  disabled={!selected || selected.state !== "bound"}
                  onClick={() => selected && setDialog({ kind: "create", ns: selected })}
                >
                  New repo
                </button>
              )}
              {mayAdopt && (
                <button
                  type="button"
                  className="secondary"
                  disabled={!selected || selected.state !== "bound"}
                  onClick={() =>
                    setDialog({ kind: "adopt", namespaceResource: selected?.resource })
                  }
                >
                  Adopt existing repo
                </button>
              )}
              {gitAdmin && (
                <Link to={BIND_PATH} className="button primary">
                  <Plus aria-hidden="true" size={16} /> Bind namespace
                </Link>
              )}
            </>
          )
        }
      />

      {nsQ.isPending && (
        <section className="card">
          <p>Loading namespaces…</p>
        </section>
      )}
      {nsQ.isError && (
        <section className="card error">
          <h3>Namespaces could not be read</h3>
          <p>
            {errorMessage(nsQ.error)}. This is a failure to ask — not a community with
            no namespaces.
          </p>
        </section>
      )}

      {nsQ.isSuccess && namespaces.length === 0 && (
        <section className="card">
          <EmptyState
            icon={FolderGit2}
            title="No namespace bound"
            action={
              gitAdmin && (
                <Link to={BIND_PATH} className="button primary">
                  Bind namespace
                </Link>
              )
            }
          >
            Bind a forge organisation or account to govern its repositories: who
            owns each, and who may commit.
            {!gitAdmin &&
              " Binding takes git.ns.admin held community-wide — a community administrator's."}
          </EmptyState>
        </section>
      )}

      {namespaces.length > 0 && (
        <section aria-label="Namespaces" className="gitns-ns-grid">
          {namespaces.map((ns) => (
            <NamespaceCard
              key={ns.id}
              ns={ns}
              repos={repos.filter((r) => r.namespace === ns.id)}
              rights={rightsQ.isSuccess ? rights : null}
              rightsError={rightsQ.error}
              selected={ns.id === selected?.id}
              policyVersion={policyVersion}
              onUnbind={() =>
                setDialog({ kind: "sign", task: unbindTask(ns.id, ns.resource) })
              }
              onReseat={() => setDialog({ kind: "reseat", ns })}
              onReproject={() =>
                setDialog({ kind: "sign", task: reprojectTask(ns.resource) })
              }
            />
          ))}
        </section>
      )}

      {selected && (
        <section className="card" aria-labelledby="gitns-repos-title">
          <div className="gitns-section-head">
            <h3 id="gitns-repos-title">{selected.resource} · repositories</h3>
            <BootstrapLegend />
          </div>
          {reposQ.isPending && <p>Loading repositories…</p>}
          {reposQ.isError && (
            <p className="muted">Repositories could not be read: {errorMessage(reposQ.error)}.</p>
          )}
          {reposQ.isSuccess && (
            <ReposTable
              ns={selected}
              repos={selectedRepos}
              onAdopt={(resource) => setDialog({ kind: "adopt", resource })}
              onAssignOwner={(resource) =>
                setDialog({
                  kind: "grant",
                  resource,
                  right: "git.repo.own",
                  title: `Assign an owner to ${shortName(resource)}`,
                })
              }
            />
          )}
        </section>
      )}

      {selected && (
        <>
          {rightsQ.isPending ? (
            <section className="card">
              <h3>Namespace rights</h3>
              <p>Loading rights…</p>
            </section>
          ) : rightsQ.isError ? (
            <section className="card">
              <h3>Namespace rights</h3>
              <p className="muted">Rights could not be read: {readErrorMessage(rightsQ.error)}.</p>
            </section>
          ) : (
            <NamespaceRights
              ns={selected}
              rights={rights}
              onGrant={(right) =>
                setDialog({
                  kind: "grant",
                  resource: selected.resource,
                  right,
                  title: `Grant ${right} on ${selected.resource}`,
                })
              }
            />
          )}
          <div className="gitns-pair">
            <BreakGlassCard />
            <DepartedCard />
          </div>
        </>
      )}

      {detached.length > 0 && (
        <section className="card" aria-labelledby="gitns-detached-title">
          <h3 id="gitns-detached-title">Detached repositories</h3>
          <p className="muted gitns-small">
            No longer governed: their namespace was unbound, or they moved outside it.
            Nothing about them is published, and the VTC keeps their records for
            history. Adopt one again by binding its namespace and adopting it there.
          </p>
          <ul className="gitns-adopt-list">
            {detached.map((r) => (
              <li key={r.id}>
                <Link to={repoPath(r.resource)} className="gitns-mono">
                  {r.resource}
                </Link>
                <span className="muted gitns-small">
                  {r.state === "detached" ? "detached" : `${r.state}, namespace unbound`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {roleDerived > 0 && (
        <p className="muted gitns-small">
          {roleDerived} commit {roleDerived === 1 ? "right comes" : "rights come"} from{" "}
          <code>[hooks.git-trust] grant_on_role</code>. They are role-derived and
          managed only through configuration; inside a bound namespace the git
          namespace projection publishes them.
        </p>
      )}

      {dialog?.kind === "grant" && (
        <GrantDialog
          resource={dialog.resource}
          rights={[dialog.right]}
          initialRight={dialog.right}
          title={dialog.title}
          onClose={() => setDialog(null)}
          onBuilt={(task) => setDialog({ kind: "sign", task })}
        />
      )}
      {dialog?.kind === "adopt" && (
        <AdoptDialog
          resource={dialog.resource}
          namespaceResource={dialog.namespaceResource}
          onClose={() => setDialog(null)}
          onBuilt={(task) => setDialog({ kind: "sign", task })}
        />
      )}
      {dialog?.kind === "create" && (
        <CreateDialog
          namespaceId={dialog.ns.id}
          namespaceResource={dialog.ns.resource}
          personal={isPersonal(dialog.ns)}
          onClose={() => setDialog(null)}
          onBuilt={(task) => setDialog({ kind: "sign", task })}
        />
      )}
      {dialog?.kind === "reseat" && (
        <ReseatDialog
          namespaceId={dialog.ns.id}
          namespaceResource={dialog.ns.resource}
          onClose={() => setDialog(null)}
          onBuilt={(task) => setDialog({ kind: "sign", task })}
        />
      )}
      {dialog?.kind === "sign" && (
        <SignTaskDialog task={dialog.task} onClose={() => setDialog(null)} />
      )}
    </>
  );
}
